import { Box } from "@mui/system";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import {
  addEvent,
  setSingleStepTestStatus
} from "~/features/testingTools/testingTools.slice";
import { ErrorItem } from "~/features/testingTools/testingTools.types";
import { runAsyncSafely } from "~/lib/helpers";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import {
  useClosePortMutation,
  useOpenPortForContentMutation,
  useRequestNextBinMutation
} from "~/redux/warehouse/autostoreGrid.hooks";

import Port, { getAlertMessageFromErrorList } from "./Port";

type WorkstationPortInheritedProps = {
  portId: number;
  binToPortTestLoopingEnabled: boolean;
  updateBinToPortLoopBtnStatus: (statusValue: boolean) => void;
};

const WorkstationPort = (props: WorkstationPortInheritedProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const portId = props.portId;

  const [openPortForContent, { error: openPortError }] =
    useOpenPortForContentMutation();
  const [getNextBin, { error: getNextBinError }] = useRequestNextBinMutation();
  const [closePort, { error: closePortError }] = useClosePortMutation();
  const workstation = useAppSelector(selectThisWorkstation);
  const portReadyDispatchedRef = useRef(false);
  const targetPorts = workstation?.ports.filter(
    (port) => port.parentPortId === portId || port.portId === portId
  );
  const parentPortId = workstation?.ports?.[0]?.parentPortId || null;

  const getNextBinForCurrentPortSafely = useCallback(async () => {
    if (!workstation) return;
    await runAsyncSafely(
      getNextBin({
        autostoreGridId: workstation.autostoreGridId,
        portId
      }).unwrap()
    );
  }, [getNextBin, portId, workstation]);

  const closePortSafely = useCallback(async () => {
    if (!workstation) return;
    await runAsyncSafely(
      closePort({
        autostoreGridId: workstation.autostoreGridId,
        portId
      }).unwrap()
    );
  }, [closePort, portId, workstation]);

  const initializePort = useCallback(async () => {
    if (!workstation) return;
    //1)First, ensure the port is closed, this is just and additional safety check
    await closePortSafely();
    //2)Open the port for content
    await runAsyncSafely(
      openPortForContent({
        autostoreGridId: workstation.autostoreGridId,
        portId,
        configurationTypes: [1]
      }).unwrap()
    );
    //3)Finally get the next bin
    await getNextBinForCurrentPortSafely();
  }, [
    closePortSafely,
    getNextBinForCurrentPortSafely,
    openPortForContent,
    portId,
    workstation
  ]);

  const errorItems: ErrorItem[] = useMemo(
    () => [
      {
        title: "Opening port for content failed",
        error: openPortError
      },
      {
        title: "Closing port failed",
        error: closePortError
      },
      {
        title: "Request next bin failed",
        error: getNextBinError,
        onRetryCb: async () => {
          await getNextBinForCurrentPortSafely();
        }
      }
    ],
    [
      closePortError,
      getNextBinError,
      getNextBinForCurrentPortSafely,
      openPortError
    ]
  );

  useEffect(() => {
    if (!workstation) return;

    void initializePort();

    return () => {
      void closePortSafely();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const errorEventMessage = openPortError
      ? `Bin to Port: failed opening port ${portId}`
      : getNextBinError
        ? `Bin to Port: failed getting next bin for port ${portId}`
        : null;
    if (!errorEventMessage) return;

    dispatch(
      addEvent({
        text: errorEventMessage,
        state: "Failed",
        testOrigin: "Bin to Port"
      })
    );
    dispatch(
      setSingleStepTestStatus({
        testingToolName: "Bin to Port",
        eventStatus: "Failed",
        skipLogging: true
      })
    );
  }, [openPortError, getNextBinError, dispatch, portId]);

  const errorMessageByType = getAlertMessageFromErrorList(
    errorItems,
    t("retry"),
    initializePort
  );

  return (
    <Box
      sx={{
        gridColumn: workstation?.multiPortEnabled ? "span 2" : "unset"
      }}
    >
      {targetPorts?.map((p, i) => (
        <Port
          key={p.portId}
          portSideIndex={i}
          portIdToDisplay={portId === parentPortId ? p.portId : portId}
          portReadyDispatchedRef={portReadyDispatchedRef}
          {...props}
        />
      ))}
      {!!errorMessageByType && errorMessageByType}
    </Box>
  );
};

export default WorkstationPort;
